import { SearchInputWithRecent } from "@editor/components/search-input-with-recent";
import type { IMediaLibDialogItem, TElement } from "@editor/modules/media-lib";
import { UnsplashIcon } from "@editor/modules/media-lib/icons";
import { useEffect } from "react";
import { useDebounceValue } from "usehooks-ts";
import { StockImagesProvider } from "../ctx";
import { useFetchStockImages, useResetStore } from "../hooks";
import { useImagesPanelStockStoreShallow } from "../store";
import type { IImagesPanelImage } from "../types";
import { ImagesPanelStockMasonry } from "./masonry";
import { StockCategories } from "./stock-categories";
import { StockError } from "./stock-error";
import { StockNotFound } from "./stock-not-found";
import { StockSkeleton } from "./stock-skeleton";

export const Stock = (context: {
  view?: "dialog" | "panel";
  onSelect?: (item: IImagesPanelImage) => void;
  selectedItems?: IMediaLibDialogItem[];
  elementWrapper?: React.ComponentType<{
    children: React.ReactNode;
    element: TElement;
  }>;
  isMultiSelect?: boolean;
}) => {
  useResetStore();
  const { columnsData, queryRaw, setQueryRaw, setCategory } =
    useImagesPanelStockStoreShallow((s) => ({
      columnsData: s.columnsData,
      queryRaw: s.queryRaw,
      setQueryRaw: s.setQueryRaw,
      setCategory: s.setCategory,
    }));

  const [searchQuery, setDebouncedSearchQuery] = useDebounceValue(
    queryRaw,
    500,
  );

  const { isError, reloadData, fetchMoreImages, shouldShowSkeleton } =
    useFetchStockImages({
      searchQuery,
    });

  useEffect(() => {
    setCategory(context.view === "dialog" ? "business" : "ai_suggestions");
  }, [context.view, setCategory]);

  const isBufferingSerach = searchQuery !== queryRaw;
  return (
    <StockImagesProvider {...context}>
      <div className="tw-relative tw-grid tw-h-full tw-grid-rows-[auto_1fr] tw-overflow-hidden tw-rounded-2xl">
        <div className="tw-grid tw-max-w-full tw-px-6 -sm:tw-px-3">
          <SearchInputWithRecent
            placeholder="Search images"
            value={queryRaw}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length === 0) {
                setDebouncedSearchQuery(value);
              }
              setQueryRaw(value);
            }}
            onSubmit={(value) => {
              setDebouncedSearchQuery(value);
              setQueryRaw(value);
            }}
            onClear={() => {
              setDebouncedSearchQuery("");
              setQueryRaw("");
            }}
            isProcessing={isBufferingSerach}
            storageKey="sd-images-panel-images-recent-search"
            amountOfStoredSearches={3}
          />

          {queryRaw.length === 0 && <StockCategories />}
        </div>
        {isError ? (
          <StockError onReload={reloadData} />
        ) : shouldShowSkeleton ? (
          <StockSkeleton />
        ) : columnsData[0].length === 0 ? (
          <StockNotFound />
        ) : (
          <ImagesPanelStockMasonry
            searchQuery={searchQuery}
            loadMoreImages={fetchMoreImages}
          />
        )}
        <div
          className="tw-absolute tw-bottom-2 tw-right-2 tw-flex tw-h-8 tw-items-center tw-justify-center tw-rounded-[40px] tw-bg-white tw-px-3 tw-py-2 tw-transition-colors"
          style={{
            boxShadow:
              "0px 0px 0px 1px rgba(18, 3, 62, 0.06), 0px 5px 1px 0px rgba(18, 3, 62, 0.00), 0px 3px 1px 0px rgba(18, 3, 62, 0.01), 0px 2px 1px 0px rgba(18, 3, 62, 0.03), 0px 1px 1px 0px rgba(18, 3, 62, 0.04)",
          }}
        >
          <UnsplashIcon className="tw-size-auto" />
        </div>
      </div>
    </StockImagesProvider>
  );
};
